import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Select, Tag, Input, Typography, Spin } from "antd";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SearchOutlined, LoadingOutlined } from "@ant-design/icons";
import EditOff from "@/assets/icons/edit_off.svg?react";
import VisibilityOff from "@/assets/icons/visibility_off.svg?react";
import AssignmentTurnedIn from "@/assets/icons/assignment_turned_in.svg?react";
import AlertIcon from "@/assets/icons/alert_icon.svg?react";
import ArrowCoolDown from "@/assets/icons/arrow_cool_down.svg?react";
import PersonIcon from "@/assets/icons/person.svg?react";
import { useUser } from "@/contexts/UserContext";
import {
  formatDateWithMicroseconds,
  formatDateTimeWithAt,
} from "@/utils/dateUtils";
import { getAvatarColor, getInitials } from "@/utils/avatarUtils";
import trialDataApiCRUDServices from "@/services/trialDataApiCRUDServices";
import { useAppNotification } from "@/hooks/useAppNotification";

const { Text } = Typography;

export type AssigneeOption = {
  label: string;
  value: string;
};

type SelectOption = {
  label: string;
  value: string;
};

import {
  EMPTY_STATUS_VALUE,
  getAssigneeNameFromId,
  getDisplayValue,
  prepareMutationValue,
} from "./dataDiscrepancies";

const useDiscrepancyMutation = ({
  successCallback,
}: {
  successCallback: (data: any) => void;
}) => {
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();
  const { id } = useScopeParams();

  const assignDiscrepancy = useMutation({
    mutationFn: async (variables: {
      discrepancy_id: string;
      [key: string]: any;
    }) => {
      const { discrepancy_id } = variables;

      let payload: any = {};
      if ("discrepancy_assignee" in variables) {
        const assigneeId = prepareMutationValue(
          variables["discrepancy_assignee"],
          "discrepancy_assignee",
        );
        payload = { assignee: assigneeId };
      } else if ("discrepancy_status" in variables) {
        const statusValue = prepareMutationValue(
          variables["discrepancy_status"],
          "discrepancy_status",
        );
        payload = { discrepancy_status: statusValue };
      }

      const response = await trialDataApiCRUDServices.patch(
        `${id}/issue_management`,
        discrepancy_id,
        payload,
      );

      if (!response.ok) {
        throw new Error("Failed to update discrepancy");
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      openSuccessNotification({
        title: (
          <p className="text-[#003832] text-base font-semibold">
            Discrepancy updated
          </p>
        ),
        description: (
          <p className="text-[#2B4752] text-[14px]">
            The discrepancy has been successfully updated.
          </p>
        ),
        placement: "bottom",
        className: "insight-success-confirmation",
        duration: 1,
        icon: null,
      });
      successCallback(variables);
    },
    onError: () => {
      openErrorNotification({
        title: (
          <p className="text-[#73134A] text-base font-semibold">
            Update failed
          </p>
        ),
        description: (
          <p className="text-[#260618] text-[14px]">
            There was an error updating the discrepancy. Please try again.
          </p>
        ),
        placement: "bottom",
        className: "insight-error-confirmation",
        duration: 1,
        icon: null,
      });
    },
  });
  return { assignDiscrepancy, contextHolder };
};

const updateIssue = (
  data: any,
  oldData: any,
  valueToUpdate: any,
  user: any,
  assigneeIdToName?: Record<string, string>,
) => {
  const existingRecord = oldData.results.find(
    (item: any) => item.discrepancy_id === data.discrepancy_id,
  );

  if (!existingRecord) return;

  const newValue =
    valueToUpdate === "discrepancy_assignee"
      ? getAssigneeNameFromId(data[valueToUpdate], assigneeIdToName)
      : data[valueToUpdate];

  return {
    ...existingRecord,
    [valueToUpdate]: newValue,
    discrepancy_assignment_last_modified_by: `${user?.userInfo?.first_name} ${user?.userInfo?.last_name}`,
    discrepancy_assignments_last_modified_datetime: formatDateWithMicroseconds(
      new Date(),
    ),
  };
};

const SelectWithRef = ({
  options,
  text,
  optionRender,
  queryKey,
  defaultValue,
  record,
  valueToUpdate,
  assigneeIdToName,
  setSelectedRecord,
  onAssignmentChange,
  dataTestId,
}: {
  options: Array<string | AssigneeOption>;
  text: string;
  optionRender: (option: { label: string; value: string }) => React.ReactNode;
  queryKey: string[];
  defaultValue: string;
  record: any;
  valueToUpdate: string;
  assigneeIdToName?: Record<string, string>;
  setSelectedRecord?: (record: any) => void;
  onAssignmentChange?: () => void;
  dataTestId?: string;
}) => {
  const user = useUser();
  const isAssigneeSelect = valueToUpdate === "discrepancy_assignee";

  const queryClient = useQueryClient();
  const { assignDiscrepancy, contextHolder } = useDiscrepancyMutation({
    successCallback: (data) => {
      queryClient.setQueryData(queryKey, (oldData: any) => {
        const updatedRecord = updateIssue(
          data,
          oldData,
          valueToUpdate,
          user,
          assigneeIdToName,
        );
        const results = oldData.results.map((item: any) =>
          item.discrepancy_id === data.discrepancy_id ? updatedRecord : item,
        );
        if (setSelectedRecord) {
          setSelectedRecord(updatedRecord);
        }
        return { ...oldData, results };
      });
      // set and normalize displayed value
      const resolvedValue =
        valueToUpdate === "discrepancy_assignee"
          ? getAssigneeNameFromId(data[valueToUpdate], assigneeIdToName)
          : data[valueToUpdate];
      const newDisplay = getDisplayValue(resolvedValue, valueToUpdate);
      setValue(createDisplayValue(newDisplay));
      if (onAssignmentChange) {
        onAssignmentChange();
      }
    },
  });

  const [search, setSearch] = useState("");

  const normalizedText = getDisplayValue(text, valueToUpdate);
  const createDisplayValue = useCallback(
    (displayText: string) => {
      const safeText = displayText || defaultValue;

      if (!isAssigneeSelect) {
        return safeText;
      }

      return {
        value: safeText,
        label: optionRender({ label: safeText, value: safeText }),
      };
    },
    [defaultValue, isAssigneeSelect, optionRender],
  );
  const [value, setValue] = useState(
    createDisplayValue(normalizedText || defaultValue),
  );

  useEffect(() => {
    setValue(createDisplayValue(normalizedText || defaultValue));
  }, [normalizedText, valueToUpdate, createDisplayValue, defaultValue]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return options
      .map((option) =>
        typeof option === "string" ? { label: option, value: option } : option,
      )
      .filter((option) => option.label.toLowerCase().includes(q))
      .map((option) => ({
        label: optionRender(option),
        value: option.value,
      }));
  }, [options, optionRender, search]);

  const handleChange = (
    selected:
      | string
      | {
          value: string;
          label?: React.ReactNode;
        },
  ) => {
    const selectedValue =
      typeof selected === "string" ? selected : selected?.value;

    assignDiscrepancy.mutate({
      discrepancy_id: record.discrepancy_id,
      [valueToUpdate]: prepareMutationValue(selectedValue, valueToUpdate),
    });
  };

  return (
    <>
      {contextHolder}
      {assignDiscrepancy.isPending ? (
        <div className="flex justify-center items-center">
          <Spin indicator={<LoadingOutlined spin />} size="medium" />
        </div>
      ) : (
        <Select
          data-testid={dataTestId}
          popupRender={(menu) => (
            <div className="py-2">
              {options.length > 6 && (
                <div className="px-2 mb-4">
                  <Input
                    autoFocus
                    value={search}
                    placeholder="Search"
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    prefix={<SearchOutlined className="text-[#BFBFBF]" />}
                  />
                </div>
              )}
              {menu}
            </div>
          )}
          style={{ width: 200 }}
          variant="borderless"
          labelInValue={isAssigneeSelect}
          options={filteredOptions}
          showSearch={{ onSearch: (value) => setSearch(value) }}
          onChange={handleChange}
          onOpenChange={(open) => {
            if (!open) setSearch("");
          }}
          value={value}
        />
      )}
    </>
  );
};

// Helper component for table rows
export const TableRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <tr
    className="divide-x divide-solid divide-[#F0F0F0]"
    data-testid={`drawer-row-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <td className="py-2 px-2  text-gray-600" style={{ width: "150px" }}>
      {label}
    </td>
    <td className="py-2 px-2">{value}</td>
  </tr>
);

export const ResolutionTag = ({ status }: { status: any }) => {
  const statusColors = {
    Resolved: { bg: "#CAE6D6", text: "#004A42" },
    Unresolved: { bg: "#EFF1F2", text: "#2B4752" },
  };
  const style = (statusColors as any)[status] || {
    bg: "#F5F5F5",
    text: "#666666",
  };
  return (
    <Tag color={style.bg} style={{ borderRadius: "100px", padding: "5px 8px" }}>
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: style.text }}
        ></div>
        <span style={{ color: style.text }}>{status}</span>
      </div>
    </Tag>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const labelRender = ({ label }: { label: any }) => {
  switch (label) {
    case EMPTY_STATUS_VALUE:
      return (
        <div className="flex items-center gap-2 text-[#8C8C8C]">
          <EditOff />
          <p className="mb-0">Empty</p>
        </div>
      );
    case "Ignore":
      return (
        <div className="flex items-center gap-2 ">
          <VisibilityOff />
          <p className="mb-0">Ignored</p>
        </div>
      );
    case "Closed":
      return (
        <div className="flex items-center gap-2 ">
          <AssignmentTurnedIn />
          <p className="mb-0">Closed</p>
        </div>
      );
    case "Priority":
      return (
        <div className="flex items-center gap-2 ">
          <AlertIcon />
          <p className="mb-0">Priority</p>
        </div>
      );
    case "In Progress":
      return (
        <div className="flex items-center gap-2 ">
          <ArrowCoolDown />
          <p className="mb-0">In Progress</p>
        </div>
      );
    default:
      // If we don't recognize the value, display it as-is but with Empty icon
      return (
        <div className="flex items-center gap-2 text-[#8C8C8C]">
          <EditOff />
          <p className="mb-0">{label || "Empty"}</p>
        </div>
      );
  }
};

export const DateRenderer = ({ dateString }: { dateString: any }) => {
  if (!dateString) {
    return "-";
  }
  return <Text>{formatDateTimeWithAt(dateString)}</Text>;
};

export const AssigneeSelect = ({
  availableAssignees,
  text,
  record,
  queryKey,
  assigneeIdToName,
  setSelectedRecord,
  onAssignmentChange,
}: {
  availableAssignees: AssigneeOption[];
  text: string;
  record: any;
  queryKey: string[];
  assigneeIdToName?: Record<string, string>;
  setSelectedRecord?: (record: any) => void;
  onAssignmentChange?: () => void;
}) => {
  return (
    <SelectWithRef
      dataTestId={`issue-assignee-select-${record.discrepancy_id}`}
      setSelectedRecord={setSelectedRecord}
      options={[
        { label: "Unassigned", value: "Unassigned" },
        ...availableAssignees,
      ]}
      defaultValue={text || "Unassigned"}
      record={record}
      queryKey={queryKey}
      valueToUpdate="discrepancy_assignee"
      assigneeIdToName={assigneeIdToName}
      onAssignmentChange={onAssignmentChange}
      optionRender={({ label }) => {
        const text = label.length > 1 ? label : "Unassigned";
        return (
          <div className="grid grid-cols-[32px_1fr] items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white"
              style={{
                backgroundColor:
                  text === "Unassigned" ? "#D5DADC" : getAvatarColor(text),
              }}
            >
              {text === "Unassigned" ? (
                <PersonIcon
                  style={{ width: "24px", height: "24px" }}
                  className="text-[#556C75]"
                />
              ) : (
                getInitials(text)
              )}
            </div>
            <Typography.Text
              ellipsis
              style={{ color: text === "Unassigned" ? "#8C8C8C" : "#000" }}
            >
              {text || "Unassigned"}
            </Typography.Text>
          </div>
        );
      }}
      text={text}
    />
  );
};

const labelOptions: SelectOption[] = [
  { label: "Empty", value: EMPTY_STATUS_VALUE },
  { label: "Ignore", value: "Ignore" },
  { label: "Priority", value: "Priority" },
  { label: "In Progress", value: "In Progress" },
  { label: "Closed", value: "Closed" },
];

export const LabelTag = ({
  text,
  record,
  queryKey,
  setSelectedRecord,
  onAssignmentChange,
}: {
  text: string;
  record: any;
  queryKey: string[];
  setSelectedRecord?: (record: any) => void;
  onAssignmentChange?: () => void;
}) => {
  return (
    <SelectWithRef
      dataTestId={`issue-status-select-${record.discrepancy_id}`}
      setSelectedRecord={setSelectedRecord}
      text={text}
      options={labelOptions}
      defaultValue={EMPTY_STATUS_VALUE}
      optionRender={labelRender}
      valueToUpdate="discrepancy_status"
      record={record}
      queryKey={queryKey}
      onAssignmentChange={onAssignmentChange}
    />
  );
};
